import type { BaseSyntheticEvent, FC, ReactElement } from "react"

import { useLocalStorage } from "react-use"

import {
    type TLogoutFunction,
    type TScreenBreakpoints,
    type TUserState,
    useLogout,
    useScreenBreakpoints,
    useUser
} from "@/3514/hooks"
import { type TProductTypeQuery, useProductTypeQuery } from "@/3514/queries"
import { isUserCoach } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { LocalStorageServiceKeys } from "@/services"

import { participantHeaderElements as elements } from "./participant.header.elements"
import { participantHeaderStyles as classes } from "./participant.header.styles"
import { EParticipantHeaderVariant as EVariant } from "./participant.header.types"

const { renderMobileChildren, renderDesktopChildren } = elements

type TParticipantHeaderProps = {
    variant?: EVariant
}

const ParticipantHeader: FC<TParticipantHeaderProps> = ({
    variant = EVariant.Default
}: TParticipantHeaderProps): ReactElement => {
    const { user }: TUserState = useUser()

    const [, setLocalStorageValue] = useLocalStorage<boolean>(LocalStorageServiceKeys.MainUserLoggedOut(), false)

    const logout: TLogoutFunction = useLogout()

    const { t }: IUseTranslation = useTranslation()

    const { data: productType }: TProductTypeQuery = useProductTypeQuery()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    function handleLogout(e: BaseSyntheticEvent): void {
        e.preventDefault()

        void logout({
            onComplete: async (): Promise<void> =>
                isUserCoach(user) && user.areSubUsersPresent ? setLocalStorageValue(true) : void 0
        })
    }

    return (
        <header className={classes.header({ variant })}>
            {breakpoints.xs || breakpoints.sm
                ? renderMobileChildren({ variant, breakpoints, t, handleLogout, productType })
                : renderDesktopChildren({ variant, breakpoints, t, handleLogout, productType })}
        </header>
    )
}

ParticipantHeader.displayName = "ParticipantHeader"

export { ParticipantHeader }
